import { useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";

// Always-visible emergency button in the header. Tap → confirm modal
// → activates Emergency Mode (LS-SPEC-03). Pre-authorised medical
// disclosure scopes (configured in Settings) become available to
// any hospital agent.

export function EmergencyButton() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activate = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const result = await mcpCall<{ activation_id: string }>(
        "emergency.activate",
        {
          consumer_id: consumerIdOrThrow(),
          trigger: "consumer_reported",
          summary: "consumer tapped emergency button",
        },
      );
      setActive(result.activation_id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const resolve = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      await mcpCall("emergency.resolve",
        { activation_id: active, resolution_note: "consumer cleared" });
      setActive(null);
      setOpen(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Emergency"
        className={[
          "rounded-(--radius-pill) px-2.5 py-0.5 text-[10px] font-medium",
          active
            ? "bg-red-500 text-white animate-pulse"
            : "bg-red-500/15 text-red-200 hover:bg-red-500/25",
        ].join(" ")}
      >
        {active ? "ACTIVE" : "SOS"}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-(--color-canvas-elev) p-5 text-(--color-ink)">
            <h2 className="text-base font-medium text-red-300">
              {active ? "Emergency active" : "Activate emergency mode"}
            </h2>
            <p className="mt-2 text-xs text-(--color-ink-muted)">
              {active
                ? "Your pre-authorised medical scopes are available to any hospital agent your scout reaches. Tap Resolve when safe."
                : "Tells Triton to find the nearest hospital, share allergies/blood-type/meds you pre-authorised, and rebook anything affected. You can resolve at any time."}
            </p>
            {err && (
              <p className="mt-2 rounded bg-red-900/40 px-2 py-1 text-xs text-red-200">{err}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="rounded-(--radius-pill) bg-transparent px-3 py-1 text-xs text-(--color-ink-muted) hover:text-(--color-ink)"
              >
                Close
              </button>
              {active ? (
                <button
                  onClick={resolve}
                  disabled={submitting}
                  className="rounded-(--radius-pill) bg-(--color-accent-gold) px-3 py-1 text-xs font-medium text-stone-900 disabled:opacity-50"
                >
                  Resolve
                </button>
              ) : (
                <button
                  onClick={activate}
                  disabled={submitting}
                  className="rounded-(--radius-pill) bg-red-500 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  Activate
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
